import axios from 'axios';
import { message } from 'antd';
import { getAccessToken, getRefreshToken, setToken, clearAuth, getUserInfo } from '../utils/auth';
import { envConfig } from '../config/env';

const BASE_URL = envConfig.apiBaseUrl;
const REQUEST_TIMEOUT = envConfig.apiTimeout;
const ENABLE_LOGGING = envConfig.enableLogging;

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const refreshTokenRequest = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuth();
    window.location.href = '/login';
    return null;
  }
  try {
    const response = await axios.post(`${envConfig.apiBaseUrl}/auth/refresh`, {
      refreshToken,
    });
    const { accessToken, refreshToken: newRefreshToken } = response.data?.data || {};
    if (accessToken) {
      setToken(accessToken, newRefreshToken);
      onRefreshed(accessToken);
      return accessToken;
    }
    clearAuth();
    window.location.href = '/login';
    return null;
  } catch {
    clearAuth();
    window.location.href = '/login';
    return null;
  }
};

const normalizeResponseData = (data) => {
  if (data === null || data === undefined) {
    return { list: [], total: 0 };
  }
  if (Array.isArray(data)) {
    return { list: data, total: data.length };
  }
  if (typeof data === 'object') {
    if (Array.isArray(data.data)) {
      return { list: data.data, total: data.total || data.data.length, ...data };
    }
    if (Array.isArray(data.list)) {
      return data;
    }
    if (Array.isArray(data.results)) {
      return { list: data.results, total: data.total || data.results.length, ...data };
    }
  }
  return data;
};

const request = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': envConfig.clientType,
  },
});

request.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 管理端审计头：后端审计依赖 X-User-Id（真实操作管理员 ID）与 X-User-Type=9
    // 网关也会从 JWT 解析注入同名头，这里显式携带作为兜底（项目硬约束）
    const info = getUserInfo();
    if (info?.id || info?.userId) {
      config.headers['X-User-Id'] = String(info.id ?? info.userId);
      config.headers['X-User-Type'] = '9';
    }

    if (ENABLE_LOGGING) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);



request.interceptors.response.use(
  (response) => {
    if (ENABLE_LOGGING) {
      console.log(`[API Response] ${response.config.url}`, response.data);
    }

    if (response.config.responseType === 'blob') {
      return response;
    }

    const { code, success, data, message: msg } = response.data || {};

    if (code === 0 || success === true || code === '0') {
      const normalizedData = normalizeResponseData(data);
      return { ...response.data, data: normalizedData };
    }

    if (code !== undefined && code !== 0) {
      message.error(msg || '请求失败');
      return Promise.reject({ code, message: msg, data });
    }

    const normalizedData = normalizeResponseData(data);
    return { ...response.data, data: normalizedData };
  },
  async (error) => {
    if (ENABLE_LOGGING) {
      console.error(`[API Error] ${error.config?.url}`, error);
      if (error.response) {
        console.error('[API Error Response]', error.response.data);
      }
    }

    const { response } = error;
    if (response) {
      const { status, data } = response;
      const errorCode = data?.code || data?.errorCode;
      const errorMsg = data?.message || data?.msg || '请求失败';

      if (status === 401 && !error.config?._retry) {
        error.config._retry = true;
        if (!isRefreshing) {
          isRefreshing = true;
          const newToken = await refreshTokenRequest();
          isRefreshing = false;
          if (newToken) {
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return request(error.config);
          }
        }
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            error.config.headers.Authorization = `Bearer ${newToken}`;
            resolve(request(error.config));
          });
        });
      }

      message.error(errorMsg);
      return Promise.reject({ status, code: errorCode, message: errorMsg, data });
    }

    if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请稍后重试');
    } else if (error.code === 'ERR_NETWORK') {
      message.error('网络连接失败，请检查网络');
    } else {
      message.error('网络连接失败，请检查网络');
    }
    return Promise.reject(error);
  }
);

request.create = (config) => {
  return axios.create({
    ...config,
    baseURL: config.baseURL || BASE_URL,
    timeout: config.timeout || REQUEST_TIMEOUT,
  });
};

request.setBaseURL = (url) => {
  request.defaults.baseURL = url;
};

/**
 * 扁平分页适配：life/join 等新接口 data 直接为数组、total/pageNum 在响应顶层，
 * 响应拦截器会把数组归一化为 { list, total: 本页长度 } 丢失真实 total，
 * 这里把顶层分页字段合并回 data，页面统一读 data.list / data.total。
 */
export const adaptFlatPage = (res) => {
  const { data } = res;
  if (data && Array.isArray(data.list) && res.total !== undefined) {
    return {
      ...res,
      data: {
        ...data,
        total: res.total,
        pageNum: res.pageNum,
        pageSize: res.pageSize,
        pages: res.pages,
        hasNext: res.hasNext,
      },
    };
  }
  return res;
};

export const createFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

export const createUrlEncoded = (data) => {
  const params = new URLSearchParams();
  Object.keys(data).forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) {
      params.append(key, data[key]);
    }
  });
  return params;
};

export default request;